// Jul 2026 — Admin-only lookup that answers "Why isn't this user receiving
// notifications?" in one click by wrapping GET /admin/notifications/diagnose.
// Renders the per-channel `willFire` verdict + `whyNot` reasons the backend
// returned so admins don't have to eyeball raw JSON.

import React, { useState } from 'react';
import { axiosInstance } from '../../App';
import {
  Bell, Mail, Award, Inbox, MapPin, ShieldAlert, ShieldCheck, Search, Loader2,
  ChevronDown, ChevronRight,
} from 'lucide-react';

const ChannelVerdict = ({ icon: Icon, label, verdict, channelSummary }) => {
  if (!verdict) return null;
  const ok = verdict.willFire;
  const summary = channelSummary;
  const hasAttempts = summary && summary.attempts > 0;
  return (
    <div
      className={`rounded-lg border p-3 ${ok ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'}`}
      data-testid={`diag-verdict-${label.toLowerCase()}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`h-4 w-4 ${ok ? 'text-emerald-600' : 'text-amber-600'}`} />
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-800">{label}</span>
        <span
          className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded ${
            ok ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'
          }`}
        >
          {ok ? 'ELIGIBLE' : "NOT ELIGIBLE"}
        </span>
      </div>
      {!ok && verdict.whyNot?.length > 0 && (
        <ul className="text-xs text-slate-700 space-y-1 pl-6 list-disc">
          {verdict.whyNot.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      {hasAttempts && (
        <div className="mt-2 pt-2 border-t border-slate-200/60 text-[11px] text-slate-700 space-y-0.5" data-testid={`diag-channel-summary-${label.toLowerCase()}`}>
          <div><span className="text-slate-500">Attempts:</span> {summary.attempts}</div>
          <div>
            <span className="text-slate-500">Last:</span>{' '}
            <span className={`font-semibold ${summary.lastResult === 'sent' ? 'text-emerald-700' : summary.lastResult === 'failed' ? 'text-red-700' : summary.lastResult === 'suppressed' ? 'text-amber-700' : 'text-slate-700'}`}>{summary.lastResult}</span>
            {' via '}<span className="font-mono text-slate-600">{summary.lastProvider}</span>
          </div>
          {summary.lastAt && (
            <div><span className="text-slate-500">At:</span> {new Date(summary.lastAt).toLocaleString()}</div>
          )}
          {summary.lastProviderMessageId && (
            <div className="truncate"><span className="text-slate-500">Msg ID:</span> <span className="font-mono">{summary.lastProviderMessageId}</span></div>
          )}
          {summary.lastError && (
            <div className="text-red-700"><span className="text-slate-500">Error:</span> {summary.lastError}</div>
          )}
        </div>
      )}
      {!hasAttempts && ok && (
        <div className="mt-2 pt-2 border-t border-slate-200/60 text-[11px] text-slate-500" data-testid={`diag-channel-no-attempts-${label.toLowerCase()}`}>
          No delivery attempt logged yet — eligibility only.
        </div>
      )}
    </div>
  );
};

const NotificationDiagnosticPanel = () => {
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState('');
  const [town, setTown] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  const run = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !town.trim()) {
      setError('Enter both an account email and a town');
      return;
    }
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const r = await axiosInstance.get('/admin/notifications/diagnose', {
        params: { email: email.trim(), town: town.trim() },
      });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Diagnostic failed');
    } finally {
      setLoading(false);
    }
  };

  const delivery = result?.delivery;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6" data-testid="notif-diag-panel">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50 transition-colors rounded-t-xl"
        data-testid="notif-diag-toggle"
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
        <ShieldAlert className="h-4 w-4 text-purple-600" />
        <span className="text-sm font-semibold text-slate-800">Notification Delivery Diagnostic</span>
        <span className="text-xs text-slate-500 ml-2">
          Why isn't a specific resident receiving push / email / badge?
        </span>
      </button>

      {expanded && (
        <div className="p-4 border-t border-slate-200 space-y-4">
          <form onSubmit={run} className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[240px]">
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Account email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="resident@example.com"
                className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                data-testid="notif-diag-email"
              />
            </div>
            <div className="w-56">
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Town
              </label>
              <input
                type="text"
                value={town}
                onChange={(e) => setTown(e.target.value)}
                placeholder="Arboleas"
                className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                data-testid="notif-diag-town"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white rounded-md transition-colors"
              data-testid="notif-diag-run"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Run diagnostic
            </button>
          </form>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="notif-diag-error">
              {error}
            </div>
          )}

          {result && (
            <div className="space-y-3">
              {/* Town resolution — show when the input was auto-corrected to canonical casing */}
              {result.townResolution && result.townResolution.canonical
                && result.townResolution.canonical !== result.townResolution.input && (
                <div
                  className="p-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900"
                  data-testid="notif-diag-canonical-hint"
                >
                  Matched <strong>{result.townResolution.input}</strong> to canonical spelling <strong>{result.townResolution.canonical}</strong>.
                </div>
              )}
              {result.townResolution && !result.townResolution.matched && (
                <div
                  className="p-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-800"
                  data-testid="notif-diag-canonical-invalid"
                >
                  <strong>{result.townResolution.input}</strong> is not a recognised Almería town. Check the spelling.
                </div>
              )}

              {/* Fan-out membership banner */}
              <div
                className={`p-2.5 rounded-md text-xs font-medium ${
                  delivery?.inTownFanout
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                    : 'bg-amber-50 text-amber-900 border border-amber-300'
                }`}
                data-testid="notif-diag-fanout-banner"
              >
                <MapPin className="h-3.5 w-3.5 inline mr-1" />
                {delivery?.inTownFanout
                  ? `Resident IS in the fan-out for town "${result.canonicalTown || result.query.town}".`
                  : `Resident is NOT in the fan-out for town "${result.canonicalTown || result.query.town}" — no device row matches this town via areaSubscriptions / followedTowns / homeTown / followRegion.`}
              </div>

              {!result.resident && (
                <div className="p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-800">
                  No user account was found for <strong>{result.query.email}</strong>. Check the spelling or ask them to re-verify their email address.
                </div>
              )}

              {result.resident && (
                <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-700 flex items-center gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-slate-500" />
                  <span>Account: <strong>{result.resident.email}</strong></span>
                  <span className="text-slate-400">•</span>
                  <span>{result.deviceCount} device row{result.deviceCount === 1 ? '' : 's'} registered</span>
                </div>
              )}

              {delivery && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <ChannelVerdict icon={Bell} label="Push" verdict={delivery.push} channelSummary={result.channelsSummary?.push} />
                  <ChannelVerdict icon={Award} label="Badge" verdict={delivery.badge} channelSummary={result.channelsSummary?.badge} />
                  <ChannelVerdict icon={Mail} label="Email" verdict={delivery.email} channelSummary={result.channelsSummary?.email} />
                  <ChannelVerdict icon={Inbox} label="Inbox" verdict={delivery.inbox} channelSummary={result.channelsSummary?.inbox} />
                </div>
              )}

              {/* Jul 2026 — Device Health */}
              {result.deviceHealth && (result.deviceHealth.issues?.length > 0 || result.deviceHealth.checks?.vapid) && (
                <div
                  className="p-3 bg-slate-50 border border-slate-200 rounded-md text-xs"
                  data-testid="notif-diag-device-health"
                >
                  <div className="flex items-center gap-1.5 font-semibold text-slate-800 mb-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-slate-600" /> Device Health
                  </div>
                  {result.deviceHealth.checks?.vapid && (
                    <div className="text-[11px] text-slate-600 mb-1.5">
                      VAPID web push subscriptions: {result.deviceHealth.checks.vapid.activeSubscriptions} active
                      {' / '}{result.deviceHealth.checks.vapid.totalSubscriptions} total
                    </div>
                  )}
                  {result.deviceHealth.issues?.length > 0 && (
                    <ul className="text-[11px] text-red-800 space-y-1 pl-4 list-disc">
                      {result.deviceHealth.issues.map((issue, i) => (
                        <li key={i}>
                          <span className="font-semibold">{issue.type}:</span> {issue.detail}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Jul 2026 — Recent delivery attempts */}
              {result.recentDeliveries && result.recentDeliveries.length > 0 && (
                <div
                  className="p-3 bg-white border border-slate-200 rounded-md text-xs"
                  data-testid="notif-diag-recent-deliveries"
                >
                  <div className="flex items-center gap-1.5 font-semibold text-slate-800 mb-1.5">
                    <Bell className="h-3.5 w-3.5 text-slate-600" />
                    Recent delivery attempts ({result.recentDeliveries.length})
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-left text-slate-500 border-b border-slate-200">
                          <th className="pb-1 pr-2">When</th>
                          <th className="pb-1 pr-2">Channel</th>
                          <th className="pb-1 pr-2">Provider</th>
                          <th className="pb-1 pr-2">Result</th>
                          <th className="pb-1 pr-2">Message ID</th>
                          <th className="pb-1">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.recentDeliveries.slice(0, 20).map((rd, i) => (
                          <tr key={i} className="border-b border-slate-100 last:border-0">
                            <td className="py-1 pr-2 text-slate-600">{new Date(rd.createdAt).toLocaleString()}</td>
                            <td className="py-1 pr-2 font-mono">{rd.channel}</td>
                            <td className="py-1 pr-2">{rd.provider}</td>
                            <td className="py-1 pr-2">
                              <span className={`inline-block px-1.5 py-0.5 rounded ${
                                rd.result === 'sent' ? 'bg-emerald-100 text-emerald-800' :
                                rd.result === 'failed' ? 'bg-red-100 text-red-800' :
                                rd.result === 'suppressed' ? 'bg-amber-100 text-amber-800' :
                                'bg-slate-100 text-slate-700'
                              }`}>{rd.result}</span>
                            </td>
                            <td className="py-1 pr-2 font-mono text-[10px] text-slate-500 max-w-[140px] truncate">{rd.providerMessageId || '—'}</td>
                            <td className="py-1 text-red-700 max-w-[220px] truncate">{rd.error || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {result.recentDeliveries && result.recentDeliveries.length === 0 && (
                <div className="p-2 bg-slate-50 border border-slate-200 rounded-md text-[11px] text-slate-600" data-testid="notif-diag-no-deliveries">
                  No delivery attempts logged yet. Any push/email/inbox send after this deploy will populate `notification_deliveries` and appear here.
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowRaw((v) => !v)}
                className="text-xs text-purple-700 hover:underline"
                data-testid="notif-diag-toggle-raw"
              >
                {showRaw ? 'Hide raw JSON' : 'Show raw JSON (advanced)'}
              </button>
              {showRaw && (
                <pre className="text-[10px] bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto max-h-96" data-testid="notif-diag-raw">
                  {JSON.stringify(result, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDiagnosticPanel;
